import { useEffect, useRef, useState } from 'react';

import { isEqual } from 'lodash';

import { listingStateSlice, useAppSelector } from '@app/app-state';
import {
    FilterCheckboxItem,
    FilterCheckboxSingleProps,
} from '@app/components/molecules/filter-checkbox-group/filter-checkbox-group.props';
import { FilterRadioItem } from '@app/components/molecules/filter-radio-group/filter-radio-group.props';
import { cancelRunWhenIdle, IdleCallbackId, runWhenIdle } from '@app/helpers/ssr.helpers';
import { useListingSearch } from '@app/hooks/useListingSearch';

type CheckboxFilterCounter = {
    items: FilterCheckboxItem[];
    selectedValue: string[];
    getPredictedValue:
        | ((value: string, checked: boolean) => string[])
        | ((value: string, checked: boolean) => number | null);
};

type RadioFilterCounter = {
    items: FilterRadioItem[];
    selectedValue: string;
    getPredictedValue: (value: string) => number | null;
};

type SearchFilterCounterProps = {
    filterKey: string;
    prependCheckbox?: FilterCheckboxSingleProps;
    isEnabled?: boolean;
} & (CheckboxFilterCounter | RadioFilterCounter);

export function useSearchFilterCounter({
    items,
    filterKey,
    prependCheckbox,
    selectedValue,
    getPredictedValue,
    isEnabled,
}: SearchFilterCounterProps) {
    const isListingItemsLoaded = useAppSelector(
        listingStateSlice.selectors.selectIsListingItemsLoaded,
    );
    const { getFilterCounter, filters, currentPage } = useListingSearch();
    const [counters, setCounters] = useState<(number | undefined)[]>(
        Array(items.length).fill(undefined),
    );
    const [prependCheckboxCounter, setPrependCheckboxCounter] = useState<number | undefined>(
        undefined,
    );
    const prevSelectedValueRef = useRef<string[] | string>(selectedValue);
    const prevExcludedParamsRef = useRef({ page: currentPage, sort: filters.sort });

    useEffect(() => {
        if (!isEnabled || !isListingItemsLoaded) {
            return;
        }

        const prevSelected = prevSelectedValueRef.current;
        const isFilterChanged = !isEqual(selectedValue, prevSelected);
        prevSelectedValueRef.current = selectedValue;

        if (isFilterChanged) {
            return;
        }

        const prevPageSort = prevExcludedParamsRef.current;
        const isPageOrSortChanged =
            currentPage !== prevPageSort.page || filters.sort !== prevPageSort.sort;
        prevExcludedParamsRef.current = { page: currentPage, sort: filters.sort };

        if (isPageOrSortChanged) {
            // Skip update when only page or sorting were changed
            return;
        }

        let cancelled = false;
        const idleCallbacks: IdleCallbackId[] = [];
        let prependCheckboxIdleCallback: IdleCallbackId | undefined = undefined;

        items.forEach((item, index) => {
            const predictedValue = Array.isArray(selectedValue)
                ? (getPredictedValue as CheckboxFilterCounter['getPredictedValue'])(
                      item.value,
                      true,
                  )
                : (getPredictedValue as RadioFilterCounter['getPredictedValue'])(item.value);

            const callbackId = runWhenIdle(() => {
                if (cancelled) {
                    return;
                }

                getFilterCounter({
                    [filterKey]: predictedValue,
                })
                    .then(count => {
                        if (cancelled) {
                            return;
                        }

                        setCounters(prev => {
                            const next = [...prev];
                            next[index] = count;
                            return next;
                        });
                    })
                    .catch(error => {
                        if (cancelled) {
                            return;
                        }

                        setCounters(prev => {
                            const next = [...prev];
                            next[index] = undefined;

                            return next;
                        });
                    });
            });

            idleCallbacks.push(callbackId);
        });

        if (prependCheckbox) {
            prependCheckboxIdleCallback = runWhenIdle(() => {
                if (cancelled) return;
                getFilterCounter({
                    [prependCheckbox.filterKey]: prependCheckbox.item.value,
                })
                    .then(count => {
                        if (cancelled) {
                            return;
                        }

                        setPrependCheckboxCounter(count);
                    })
                    .catch(error => {
                        if (cancelled) {
                            return;
                        }

                        setPrependCheckboxCounter(undefined);
                    });
            });
        }

        return () => {
            cancelled = true;
            idleCallbacks.forEach(callbackId => cancelRunWhenIdle(callbackId));

            if (!prependCheckboxIdleCallback) {
                return;
            }

            cancelRunWhenIdle(prependCheckboxIdleCallback);
        };
    }, [
        currentPage,
        filterKey,
        filters.sort,
        getFilterCounter,
        getPredictedValue,
        isListingItemsLoaded,
        items,
        prependCheckbox,
        selectedValue,
    ]);

    return {
        counters,
        prependCheckboxCounter,
    };
}
